import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { BottoniEsito } from "@/features/resi/BottoniEsito";
import {
  leggiEsito,
  nomeEsito,
  salvaEsito,
  type Esito,
  type StatoEsito,
} from "@/features/resi/esitiApi";

/**
 * «Che fine fa la merce?» (Davide 03/11/2026), nella pagina ordine. Compare solo se
 * l'ordine è partito e poi è tornato o rimborsato e nessuno ha ancora risposto. Un clic
 * salva e il riquadro si chiude; la proposta di OM è il tasto verde.
 */
export function EsitoMerceBanner({ riferimento }: { riferimento: string }) {
  const [stato, setStato] = useState<StatoEsito | null>(null);
  const [occupato, setOccupato] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<Esito | null>(null);

  useEffect(() => {
    let vivo = true;
    setFatto(null);
    leggiEsito(riferimento)
      .then((s) => vivo && setStato(s))
      // Il riquadro è un aiuto: se non si carica, la pagina ordine funziona lo stesso.
      .catch(() => vivo && setStato(null));
    return () => {
      vivo = false;
    };
  }, [riferimento]);

  async function scegli(esito: Esito) {
    setOccupato(true);
    setErrore(null);
    try {
      await salvaEsito({ riferimento }, esito);
      setFatto(esito);
      setStato(null);
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setOccupato(false);
    }
  }

  if (fatto) {
    return (
      <p
        role="status"
        className="mb-4 rounded-lg border border-green/30 bg-green-soft px-4 py-2.5 text-[13px] text-ink"
      >
        Salvato: {nomeEsito(fatto)}.
      </p>
    );
  }
  if (!stato || stato.stato !== "da_decidere") return null;

  return (
    <section
      aria-labelledby="esito-merce-titolo"
      className="mb-4 rounded-xl border border-amber-500/50 bg-amber-50 px-4 py-3.5"
    >
      <h3 id="esito-merce-titolo" className="text-[15px] font-bold leading-tight text-ink">
        Che fine fa la merce?
      </h3>
      <p className="mb-3 mt-1 text-[13px] text-ink">
        {stato.proposta ? (
          <>
            Propongo: <strong>{nomeEsito(stato.proposta)}</strong>. {stato.perche}
          </>
        ) : (
          "Finché non lo dici, la BI la conta persa."
        )}
      </p>
      <BottoniEsito
        proposta={stato.proposta}
        disabled={occupato}
        onScegli={(e) => void scegli(e)}
      />
      {errore && (
        <p role="alert" className="mt-2 text-[12px] text-red">
          {errore}
        </p>
      )}
    </section>
  );
}
